import { login } from "@lib/data/customer"
import { LOGIN_VIEW } from "@modules/account/templates/login-template"
import ErrorMessage from "@modules/checkout/components/error-message"
import { SubmitButton } from "@modules/checkout/components/submit-button"
import GoogleAuthButton from "@modules/account/components/google-auth-button"
import { useActionState } from "react"
import { useSearchParams } from "next/navigation"
import { Mail, Lock, Eye } from "lucide-react"

type Props = {
  setCurrentView: (view: LOGIN_VIEW) => void
}

const Login = ({ setCurrentView }: Props) => {
  const [formState, formAction] = useActionState(login, null)
  const searchParams = useSearchParams()
  const googleError =
    searchParams.get("error") === "google"
      ? "Google sign-in failed. Please try again."
      : null

  return (
    <div data-testid="login-page">
      <h2 className="text-2xl font-black text-center text-gray-900 mb-1 uppercase tracking-wide">
        Sign In With Email
      </h2>
      <p className="text-center text-sm text-gray-500 mb-8">
        Welcome back to the squad
      </p>

      <ErrorMessage
        error={formState?.error ?? googleError}
        data-testid="login-error-message"
        className="mb-6 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
      />

      <form className="w-full" action={formAction}>
        <div className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              E-Mail
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                name="email"
                type="email"
                placeholder="E-Mail"
                required
                autoComplete="email"
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                data-testid="email-input"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                name="password"
                type="password"
                placeholder="Password"
                required
                autoComplete="current-password"
                className="w-full pl-11 pr-11 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                data-testid="password-input"
              />
              <Eye className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 cursor-pointer" />
            </div>
          </div>
        </div>

        {/* Remember me + Forgot */}
        <div className="flex items-center justify-between mt-4 mb-6">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
            Remember me
          </label>
          <button type="button" className="text-sm text-brand-accent hover:underline font-medium">
            Forgotten Password?
          </button>
        </div>

        <SubmitButton
          data-testid="sign-in-button"
          className="w-full py-3.5 bg-[#1a1a2e] text-white font-bold rounded-lg hover:bg-[#2a2a3e] transition-colors flex items-center justify-center gap-2"
        >
          Login
          <span aria-hidden="true">&rarr;</span>
        </SubmitButton>
      </form>

      {/* OR divider */}
      <div className="flex items-center gap-4 my-6">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400 uppercase">or</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      <p className="text-center text-sm text-gray-500 mb-4">Sign In With...</p>

      <GoogleAuthButton />
    </div>
  )
}

export default Login
